import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { useIntegrationsStore } from 'stores/integrations.js'

const EMPTY = { connected: false, status: null, key_hint: null, verified_at: null, connected_at: null }

// The server's reason for a refusal. A 4xx or 5xx makes the request reject, and the answer is then
// on `error.data`, not on `response`.
const messageOf = (res, fallback) => res.response?.message || res.error?.data?.message || fallback

// The user's OpenAI key, as the page needs it. The key itself never comes back from the server:
// only whether it is connected, whether OpenAI still accepts it, and its last four characters.
// Every action returns { ok, message }.
export const useOpenAiStore = defineStore('openai', {
  state: () => ({
    connection: { ...EMPTY },
    loaded: false,
    loading: false,
    error: null,
    working: false, // an action is waiting on the server (and on OpenAI behind it)
  }),

  getters: {
    // A key is saved, but OpenAI stopped accepting it.
    stale: (state) => state.connection.status === 'invalid',
    hasKey: (state) => Boolean(state.connection.key_hint),
  },

  actions: {
    async fetch() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/integrations/openai', {}, false)
        if (!res.js_error && res.response.success) {
          this.connection = res.response.data
          this.loaded = true
        } else {
          this.error = messageOf(res, 'Failed to load the OpenAI integration.')
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    // Sends the key to the server, which checks it with OpenAI before saving it.
    connect(apiKey) {
      return this.act('POST', '/api/v1/integrations/openai', { api_key: apiKey }, 'Could not connect OpenAI.')
    },

    // Asks OpenAI again whether the saved key still works.
    check() {
      return this.act('POST', '/api/v1/integrations/openai/test', {}, 'Could not check the key.')
    },

    disconnect() {
      return this.act('DELETE', '/api/v1/integrations/openai', {}, 'Could not disconnect OpenAI.')
    },

    async act(method, path, body, fallback) {
      this.working = true
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest(method, path, body, false)
        // A failed check still says what became of the key (it may now be marked invalid).
        const data = res.response?.data ?? res.error?.data?.data
        if (data && 'connected' in data) this.connection = data
        // The integrations list shows Connected on the card: it refetches next time it is opened.
        useIntegrationsStore().invalidate()

        if (!res.js_error && res.response.success) return { ok: true, message: res.response.message }
        return { ok: false, message: messageOf(res, fallback) }
      } finally {
        this.working = false
      }
    },
  },
})
